import { Close, Print } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { differenceInHours, format } from 'date-fns';
import { increment } from 'firebase/firestore';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import uniqid from 'uniqid';
import { Shift, Wallet, WalletLog } from '../../apiTypes';
import DataTable, { Column } from '../../components/DataTable';
import PrimaryButton from '../../components/PrimaryButton';
import TransactionCategories from '../../constants/transactionCategories';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { createBatch } from '../../utils/api';
import currency from '../../utils/currency';
import { SalaryItem } from './Salary';

type Props = {
  items: SalaryItem[];
  onClose: () => void;
};

type Row = SalaryItem;

const PaySalaryModal = ({ items, onClose }: Props) => {
  const { lang } = useLocale();
  const { workersMap, wallets, walletsMap, worker } = useCafe();
  const { cafe, cafeSlug } = useAdmin();
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const print = useReactToPrint({ content: () => ref.current });
  const [checked, setChecked] = useState(false);
  const [walletId, setWalletId] = useState<string | null>(null);

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'date',
        title: t.date[lang],
        align: 'left',
        render: (r: Row) => format(r.start, 'dd.MM.yyy'),
      },
      {
        name: 'workerName',
        title: t.worker[lang],
        align: 'left',
        render: (r: Row) => workersMap[r.workerId]?.name,
      },
      {
        name: 'hours',
        title: t.hours[lang],
        render: (r: Row) => differenceInHours(r.end, r.start),
      },
      {
        name: 'total',
        title: t.total[lang],
        render: (r: Row) => currency(r.total, cafe?.currency),
      },
    ],
    [cafe?.currency, lang, workersMap]
  );

  const total = useMemo(() => items.reduce((acc, i) => acc + i.total, 0), [items]);

  const handlePay = useCallback(async () => {
    setLoading(true);
    const batch = createBatch();
    const paidTime = new Date().getTime();

    items.forEach((i) => {
      batch.update<Shift>(`cafes/${cafeSlug}/shifts/${i.id}`, { paid: paidTime });
    });

    if (checked && walletId && worker) {
      const log: WalletLog = {
        time: new Date().getTime(),
        sum: -total,
        wallet: walletId,
        category: TransactionCategories.salary,
        bankCommission: 0,
        total: (walletsMap[walletId]?.balance || 0) - total,
        info: {
          type: 'shift',
          shiftIds: items.map((i) => i.id),
        },
        workerId: worker.id,
        workerName: worker.name,
        type: 'shift',
        comment: '',
      };

      batch.update<Wallet>(`cafes/${cafeSlug}/wallets/${walletId}`, { balance: increment(-total) });
      batch.set<WalletLog>(`cafes/${cafeSlug}/wallets/${walletId}/logs/${uniqid()}`, log);
    }

    await batch.commit();

    setLoading(false);
    onClose();
  }, [cafeSlug, checked, items, onClose, total, walletId, walletsMap, worker]);

  return (
    <Dialog onClose={onClose} open fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div ref={ref}>
          <DataTable isCard={false} columns={columns} rows={items} dataTestIdPrefix="shift" />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
            <Typography variant="h6">{t.total[lang]}:</Typography>
            <Typography variant="h6">{currency(total, cafe?.currency)}</Typography>
          </Box>
        </div>

        <Box sx={{ m: 3 }}>
          <FormControlLabel
            control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
            label={t.deduct_from_wallet[lang]}
          />

          <Autocomplete
            value={walletId}
            options={wallets.map((w) => w.id)}
            getOptionLabel={(o) => walletsMap[o]?.name || ''}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label={t.wallet[lang]}
                required={checked}
                size="small"
                sx={{ width: 300, mt: 1 }}
              />
            )}
            onChange={(_, value) => setWalletId(value)}
            disabled={!checked}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <PrimaryButton startIcon={<Print />} onClick={print}>
          {t.print[lang]}
        </PrimaryButton>
        <PrimaryButton variant="contained" onClick={handlePay} disabled={loading || (checked && !walletId)}>
          {t.conduct_payment[lang]}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default PaySalaryModal;
